import moment from "moment";
import queryString from "query-string";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import generalOptionSets from "../../options/general.json";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldText,
  getFieldValue,
  getYearRangeAsArray,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import { Input, RadioButton, Select, TextArea } from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import {
  getFormsySubmissionsByFormIdForClient,
  getIncidents,
} from "../services/soar";
import { getSchema } from "./joi/validation";

function ALMonthlyReportv2() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadStatus, setLoadStatus] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadStatus.loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadStatus((ls) => ({ ...ls, loadingConfig: true }));
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadStatus((ls) => ({ ...ls, loadingConfig: false }));

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadStatus.loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [soarIncidentReports, setSoarIncidentReports] = useState([]);
  const [incidentReports, setIncidentReports] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    const loadReports = async () => {
      const signal = controller.signal;
      const responses = await Promise.all([
        getFormsySubmissionsByFormIdForClient(
          "04E93BD0-7859-EE11-830D-DC98408D5418", // AL Incident Report Form Container Id
          clientid,
          signal
        ),
        getIncidents(clientid, signal),
      ]);

      if (signal.aborted) {
        return;
      }

      if (responses?.length === 2) {
        const formsyIncidents = JSON.parse(responses[0]);

        if (formsyIncidents?.FormSubmissions?.Submission?.length > 0) {
          const activeReports =
            formsyIncidents.FormSubmissions.Submission.filter(
              (ir) => ir.Active === "1" && ir.FinalSigned === "1"
            );
          setIncidentReports(activeReports);
        }

        const soarIncidents = responses[1];
        soarIncidents.sort((a, b) => {
          const aDate = moment(getFieldDate(a.ccx_dateandtimeofincident));
          const bDate = moment(getFieldDate(b.ccx_dateandtimeofincident));

          return aDate.isBefore(bDate) ? 1 : bDate.isBefore(aDate) ? -1 : 0;
        });
        setSoarIncidentReports(soarIncidents);
      }
    };

    loadReports();
    return () => {
      controller.abort();
    };
  }, [clientid]);

  const processFormsyIncidentReports = useCallback(() => {
    setData((d) => {
      const obj = { ...d };

      if (!getFieldValue(obj.Month) || !getFieldValue(obj.Year)) {
        return obj;
      }

      const startDate = moment(
        `${getFieldValue(obj.Month)}/1/${getFieldValue(obj.Year)}`
      );
      const endDate = moment(
        `${getFieldValue(obj.Month)}/1/${getFieldValue(obj.Year)}`
      ).endOf("month");

      const monthlyReports = incidentReports.filter((ir) => {
        const dataResult = ir.FormResults.results;
        const incidentDate = moment(getFieldValue(dataResult.DateOfIncident));

        return (
          getFieldValue(dataResult.SelectedProgram) ===
            getFieldValue(obj.SelectedProgram) &&
          incidentDate.isSameOrAfter(startDate) &&
          incidentDate.isSameOrBefore(endDate)
        );
      });

      if (monthlyReports.length === 0) {
        return obj;
      }

      const processedReports = [];

      monthlyReports
        .sort((a, b) => {
          const aDate = moment(
            getFieldValue(a.FormResults.results.DateOfIncident)
          );
          const bDate = moment(
            getFieldValue(b.FormResults.results.DateOfIncident)
          );

          return aDate.isBefore(bDate) ? -1 : bDate.isBefore(aDate) ? 1 : 0;
        })
        .forEach((ir) => {
          const dataResult = ir.FormResults.results;
          const dateOfIncident = getFieldValue(dataResult.DateOfIncident);
          const typeOfIncident = getFieldValue(dataResult.IncidentType);
          const description = getFieldValue(dataResult.IncidentDescription);
          let report = `${moment(dateOfIncident).format(
            "l"
          )} - ${typeOfIncident}: ${description}`;
          processedReports.push(report);
        });

      obj.IncidentReports["@value"] = `${
        obj.IncidentReports["@value"]
          ? `${obj.IncidentReports["@value"]}

`
          : ""
      }${processedReports.join(`

`)}`;
      return obj;
    });
  }, [incidentReports]);

  const processSoarIncidentReports = useCallback(() => {
    setData((d) => {
      const obj = { ...d };

      if (!getFieldValue(obj.Month) || !getFieldValue(obj.Year)) {
        return obj;
      }

      const startDate = moment(
        `${getFieldValue(obj.Month)}/1/${getFieldValue(obj.Year)}`
      );
      const endDate = moment(
        `${getFieldValue(obj.Month)}/1/${getFieldValue(obj.Year)}`
      ).endOf("month");

      const monthlyReports = soarIncidentReports.filter((report) => {
        const incidentDateTime = moment(
          getFieldDate(report.ccx_dateandtimeofincident)
        );

        return (
          getFieldText(report.sor_clientprogram) ===
            getFieldValue(obj.SelectedProgram) &&
          incidentDateTime.isSameOrAfter(startDate) &&
          incidentDateTime.isSameOrBefore(endDate)
        );
      });

      obj.IncidentReports["@value"] = monthlyReports.map((report) => {
        const incidentDateTime = moment(
          getFieldDate(report.ccx_dateandtimeofincident)
        );
        return `${incidentDateTime.format("MMM. Do")} - ${
          report.sor_soarincidenttype["@name"]
        }`;
      }).join(`
`);

      return obj;
    });
  }, [soarIncidentReports]);

  const handleReportDateChange = (e) => {
    handleChange(e);
    processSoarIncidentReports();
    processFormsyIncidentReports();
  };

  const yesNoOptions = [
    { _id: "1", name: "Yes" },
    { _id: "0", name: "No" },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <Select
                    name="Month"
                    label="Month"
                    options={generalOptionSets.month}
                    error={errors.Month}
                    value={data.Month["@value"]}
                    onChange={handleReportDateChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Select
                    name="Year"
                    label="Year"
                    options={getYearRangeAsArray(2021)
                      .sort((a, b) => b - a)
                      .map((year) => {
                        const yearStr = `${year}`;
                        return { _id: yearStr, name: yearStr };
                      })}
                    error={errors.Year}
                    value={data.Year["@value"]}
                    onChange={handleReportDateChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="h4 mt-4 mb-2">Social Interaction / Skills</div>
              <TextArea
                name="SocialInteraction"
                label="Describe the interactions with siblings, peers, and friends in the neighborhood. Are interactions positive or negative? Describe characteristics: bossy, pushy, reserved, quiet, get taken advantage of, display aggression, etc.:"
                error={errors.SocialInteraction}
                value={data.SocialInteraction["@value"]}
                onChange={handleChange}
                identical={compare("SocialInteraction")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h4 mt-4 mb-2">
                Community and Family Activities by Child and Foster Family
              </div>
              <TextArea
                name="Activities"
                label="Describe extracurricular activities (cultural, athletic, religious, school); Do they volunteer in the community? How are these activities helping the child improve?"
                error={errors.Activities}
                value={data.Activities["@value"]}
                onChange={handleChange}
                identical={compare("Activities")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h4 mt-4 mb-2">Legal</div>
              <TextArea
                name="Legal"
                label="Describe any legal involvement? (parole or probation or if child is represented by attorney). Do biological parents still maintain their parental rights or are they terminated? Are they up for adoption? Are they at an age where they can choose not to be adopted? Do they have siblings they are not placed with?"
                error={errors.Legal}
                value={data.Legal["@value"]}
                onChange={handleChange}
                identical={compare("Legal")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h4 mt-4 mb-2">Education</div>
              <TextArea
                name="Education"
                label="General grades and is the child at an age appropriate grade level. Is the child credit deficient? Any behavioral issues noted in school adjustment? Date, purpose, and result of any school conferences and who was in attendance. IEP/504 plan? Special accommodations? If no, is one needed?"
                error={errors.Education}
                value={data.Education["@value"]}
                onChange={handleChange}
                identical={compare("Education")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h4 mt-4 mb-2">Facility</div>
              <TextArea
                name="Facility"
                label="Any children with special needs? Is the foster home stable? If not, describe risk of disruption or need for potential placement move. Is the current placement appropriate to meet the client's needs? If no, explain. Safety issues?"
                error={errors.Facility}
                value={data.Facility["@value"]}
                onChange={handleChange}
                identical={compare("Facility")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h4 mt-4 mb-2">Independent Living</div>
              <TextArea
                name="IndependentLiving"
                label="Is the client being exposed to independent living skills?(Y/N) Include if they have taken IL Classes yet or will be scheduled to do so. Do they have a job or volunteer? Is the client being exposed to independent living skills?(Y/N)"
                error={errors.IndependentLiving}
                value={data.IndependentLiving["@value"]}
                onChange={handleChange}
                identical={compare("IndependentLiving")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h4 mt-4 mb-2">
                Physical Health, Disabilities and Medication
              </div>
              <TextArea
                name="Health"
                label="Is the child up to date on all medical appointments? How is the client's physical health? Any allergies to foods or the environment? Discuss the overall health and disabilities of the child. Do they frequently get sick? Discuss developmental, emotional, or behavioral issues. Services provided to address presenting problems. Counselor and/or psychiatrist. Date last seen, when is next appointment. Date of last optical, auditory, dental, physician, other appointments."
                error={errors.Health}
                value={data.Health["@value"]}
                onChange={handleChange}
                identical={compare("Health")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h4 mt-4 mb-2">Behavior and Growth</div>
              <TextArea
                name="Behavior"
                label="Discipline/behavior resolution. Psycho-social and behavior skill development focused on in this month (quarter, etc.) Does the client display age appropriate behavior? Does the client have appropriate interaction with peers? Any school suspensions? If yes, is the client eligible for expulsion?"
                error={errors.Behavior}
                value={data.Behavior["@value"]}
                onChange={handleChange}
                identical={compare("Behavior")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h4 mt-4 mb-2">
                Primary Family Contact/Visitation
              </div>
              <TextArea
                name="PrimaryFamily"
                label="Discuss the services provided, when? Outcome of the services? Who are they allowed to contact? Face-to-face/ phone contact? Who is the child not allowed to contact? Report Progress towards reunification."
                error={errors.PrimaryFamily}
                value={data.PrimaryFamily["@value"]}
                onChange={handleChange}
                identical={compare("PrimaryFamily")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h4 mt-4 mb-2">Individual or Group Therapy</div>
              <TextArea
                name="Therapy"
                label="If child is receiving individual or group therapy, describe the type and frequency progress, participation, regression, issues addressed. List name of therapy provider."
                error={errors.Therapy}
                value={data.Therapy["@value"]}
                onChange={handleChange}
                identical={compare("Therapy")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h4 mt-4 mb-2">Recommendations and Next Steps</div>
              <TextArea
                name="Recommendations"
                label="What are recommended services for the child at this time (academic, social, therapeutic, etc)?"
                error={errors.Recommendations}
                value={data.Recommendations["@value"]}
                onChange={handleChange}
                identical={compare("Recommendations")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h4 mt-4 mb-2">Contacts / Visits</div>
              <TextArea
                name="ContactsVisits"
                label="Contact date, time, duration, and contact method, those present."
                error={errors.ContactsVisits}
                value={data.ContactsVisits["@value"]}
                onChange={handleChange}
                identical={compare("ContactsVisits")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h4 mt-4 mb-2">
                Interventions Between Recipient, SAFY Staff and Foster Parents
              </div>
              <TextArea
                name="Interventions"
                label="Interventions between Recipient, SAFY Staff, and Foster Parents."
                error={errors.Interventions}
                value={data.Interventions["@value"]}
                onChange={handleChange}
                identical={compare("Interventions")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h4 mt-4 mb-2">Incident Reports (if any):</div>
              <TextArea
                name="IncidentReports"
                error={errors.IncidentReports}
                value={data.IncidentReports["@value"]}
                onChange={handleChange}
                identical={compare("IncidentReports")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h4 mt-4 mb-2">
                Service Goals (as noted in Treatment Plan)
              </div>
              <TextArea
                name="ServiceGoals"
                label="For each goal, provide a narrative discussion, progress summary, family cooperativeness, and recommendation."
                error={errors.ServiceGoals}
                value={data.ServiceGoals["@value"]}
                onChange={handleChange}
                identical={compare("ServiceGoals")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h4 mt-4 mb-2">Outcomes</div>
              <RadioButton
                name="ReportOfStaffAbuse"
                label="Did SAFY receive a report this month that substantiated a previous allegation of abuse by staff?"
                options={yesNoOptions}
                error={errors.ReportOfStaffAbuse}
                value={data.ReportOfStaffAbuse["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="ReportOfNonStaffAbuse"
                label="Did SAFY receive a report this month that substantiated a previous allegation of abuse by non-staff?"
                options={yesNoOptions}
                error={errors.ReportOfNonStaffAbuse}
                value={data.ReportOfNonStaffAbuse["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="UnplannedDischarge"
                label="If recipient discharged this month, did SAFY request an unplanned discharge?"
                options={yesNoOptions}
                error={errors.UnplannedDischarge}
                value={data.UnplannedDischarge["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.UnplannedDischarge["@value"] === "1" && (
                <Input
                  name="LengthOfStay"
                  label="If yes, what was length of stay for this recipient?"
                  error={errors.LengthOfStay}
                  value={data.LengthOfStay["@value"]}
                  onChange={handleChange}
                  identical={compare("LengthOfStay")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <RadioButton
                name="VisitationPlan"
                label="Does this client have a visitation plan?"
                options={yesNoOptions}
                error={errors.VisitationPlan}
                value={data.VisitationPlan["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.VisitationPlan["@value"] === "1" && (
                <>
                  <Input
                    name="VisitationFrequency"
                    label="If yes, what is the frequency of visitation?"
                    error={errors.VisitationFrequency}
                    value={data.VisitationFrequency["@value"]}
                    onChange={handleChange}
                    identical={compare("VisitationFrequency")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <RadioButton
                    name="VisitationGoal"
                    label="If yes, did this recipient achieve the monthly goal of the visitation plan?"
                    options={yesNoOptions}
                    error={errors.VisitationGoal}
                    value={data.VisitationGoal["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              <RadioButton
                name="Runaway"
                label="Did client runaway this month?"
                options={yesNoOptions}
                error={errors.Runaway}
                value={data.Runaway["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.DSSRequest && (
                <RadioButton
                  name="DSSRequest"
                  label="If client discharged this month, did DSS request discharge prior to program completion?"
                  options={yesNoOptions}
                  error={errors.DSSRequest}
                  value={data.DSSRequest["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              {data.DHRRequest && (
                <RadioButton
                  name="DHRRequest"
                  label="If client discharged this month, did DHR request discharge prior to program completion?"
                  options={yesNoOptions}
                  error={errors.DHRRequest}
                  value={data.DHRRequest["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default ALMonthlyReportv2;
